/**
 * 순위 비교 — 점 + 신뢰구간 (DESIGN §8.6.2).
 *
 * **막대가 아니라 점인 이유.** 실측 가동률이 0.986~1.000 에 몰려 있다. 0 에서
 * 시작하는 막대는 40개가 전부 같은 길이로 보이고, 축을 잘라 확대한 막대는
 * 길이비를 거짓말한다. 점은 면적이 없으므로 축을 확대해도 왜곡이 없다.
 *
 * **신뢰구간을 함께 그리는 이유.** 1위 99.66% 와 3위 99.57% 의 차이가 관측량
 * 때문인지 실제 차이인지 점만 봐서는 알 수 없다. 구간이 겹치면 순위를 주장할
 * 수 없다 — 그 사실을 채워진 점 / 빈 점으로 드러낸다.
 */

import { useQuery } from '@tanstack/react-query'
import type { EChartsOption } from 'echarts'
import { useMemo, useState } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { EChart } from '@/components/EChart'
import { Headline } from '@/components/WidgetShell'
import { fetchEnvelope } from '@/lib/api'
import { axisStyle, niceBand } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { int, markSize, minutes, pct, pctAxis } from '@/lib/fmt'
import { useEntityLabel } from '@/lib/labels'
import {
  RANK_ENTITIES,
  VERDICT_LABEL,
  type Envelope,
  type RankEntity,
  type RankingData,
  type RankingItem,
  type SummaryData,
} from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'

const TOP_N = 14

/**
 * 정렬 기준.
 *
 * **왜 두 가지가 필요한가.** 값 순으로 세우면 충전기 1기짜리가 가동률 100% 로 1위가
 * 된다 — 관측이 얇을 뿐인데 최고로 보인다. 점 크기(√n)와 수염이 그 사실을 알려주긴
 * 하지만, 순서 자체는 여전히 오해를 만든다.
 *
 * 신뢰 하한 순은 "관측량을 감안해도 최소 이만큼은 된다" 로 세운다. 관측이 얇으면
 * 하한이 낮아 자연히 내려간다. 다만 제목이 "가동률 순위" 이므로 **기본값은 값 순**
 * 으로 두고, 대안을 눈에 보이게 제공한다 — 라벨과 다른 정렬을 조용히 쓰지 않는다.
 */
const SORTS = [
  { key: 'value', label: '값 순', axis: '가동률 내림차순' },
  { key: 'lower', label: '신뢰 하한 순', axis: '95% 신뢰구간 하한 내림차순' },
] as const

type SortKey = (typeof SORTS)[number]['key']

export function RankingCompare({ entity }: { entity: RankEntity }) {
  const filters = useFilters()
  const [sort, setSort] = useState<SortKey>('value')
  const labelOf = useEntityLabel()

  const q = useQuery({
    queryKey: ['metrics/ranking', entity, filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<RankingData>('/metrics/ranking', toQueryFilters(filters), { entity }, signal),
    staleTime: 5 * 60 * 1000,
  })
  // 기준선은 프론트에서 평균을 내지 않는다 — 백엔드가 계산한 전체 값을 그대로 쓴다
  const overallQ = useQuery({
    queryKey: ['metrics/summary', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<SummaryData>('/metrics/summary', toQueryFilters(filters), {}, signal),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<RankingData> | undefined
  const overall = (overallQ.data as Envelope<SummaryData> | undefined)?.data.availability ?? null

  const ranked = useMemo(() => {
    const key = (r: RankingItem) => (sort === 'lower' ? r.availability_ci[0] : (r.availability ?? 0))
    return [...(env?.data.ranking ?? [])]
      .filter((r) => r.availability != null)
      .sort((a, b) => key(b) - key(a))
  }, [env, sort])
  const excluded = env?.data.excluded ?? []
  const shown = ranked.slice(0, TOP_N)
  const axisLabel = RANK_ENTITIES.find((e) => e.key === entity)?.label ?? '대상'
  const sortMeta = SORTS.find((x) => x.key === sort) ?? SORTS[0]
  // 구간이 미세해 화면에서 안 보일 수 있으므로 결론은 숫자로도 말한다
  const nDistinct = ranked.filter(
    (r) => overall != null && (r.availability_ci[0] > overall || r.availability_ci[1] < overall),
  ).length

  /** 가로축 구간. 각주가 "지금 어디까지 확대했나"를 말해야 하므로 밖으로 꺼낸다 */
  const bandShown = useMemo(() => {
    if (shown.length === 0) return { min: 0, max: 1, interval: 0.2 }
    const los = shown.map((r) => r.availability_ci[0])
    const his = shown.map((r) => r.availability_ci[1])
    const lo = Math.min(...los, overall ?? 1)
    const hi = Math.max(...his, overall ?? 0)
    const pad = Math.max((hi - lo) * 0.08, 0.0005)
    return niceBand(Math.max(0, lo - pad), Math.min(1, hi + pad))
  }, [shown, overall])

  const option = useMemo<EChartsOption>(() => {
    /** 구간이 전체 기준선을 건너지 않으면 "전체와 다르다"고 말할 수 있다 */
    const distinguishable = (r: RankingItem) =>
      overall != null && (r.availability_ci[0] > overall || r.availability_ci[1] < overall)

    const rows = [...shown].reverse() // ECharts 카테고리축은 아래에서 위로 쌓인다
    const accent = cssVar('--c-accent', '#0066cc')
    const ink3 = cssVar('--c-ink-3', '#6e6e73')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    const canvas = cssVar('--c-canvas', '#ffffff')

    const band = bandShown

    return {
      // top 은 "전체" 기준선 라벨 자리다 — 좁히면 라벨이 잘린다
      grid: { left: 116, right: 72, top: 26, bottom: 26 },
      tooltip: {
        trigger: 'item',
        formatter: (p) => {
          const idx = (p as { dataIndex: number }).dataIndex
          const r = rows[idx]
          if (!r) return ''
          return [
            `<b>${labelOf(entity, r.entity_id)}</b>`,
            `가동률 ${pct(r.availability)}`,
            `95% 구간 ${pct(r.availability_ci[0])} ~ ${pct(r.availability_ci[1])}`,
            `충전기 ${int(r.n_chargers)}기 · 커버리지 ${pct(r.coverage, 1)}`,
          ].join('<br/>')
        },
      },
      xAxis: {
        type: 'value',
        min: band.min,
        max: band.max,
        interval: band.interval,
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, formatter: pctAxis(band.interval * 5) },
      },
      yAxis: {
        type: 'category',
        data: rows.map((r) => labelOf(entity, r.entity_id)),
        ...axisStyle(ink3, hairline),
        splitLine: { show: false },
        axisLabel: {
          fontSize: 11,
          color: cssVar('--c-ink-2', '#4a4a4f'),
          width: 108,
          overflow: 'truncate',
        },
      },
      series: [
        {
          type: 'custom',
          // [구간하한, 구간상한, 값, 카테고리인덱스, 전체와구분가능, 충전기수]
          data: rows.map((r, i) => [
            r.availability_ci[0],
            r.availability_ci[1],
            r.availability as number,
            i,
            distinguishable(r) ? 1 : 0,
            r.n_chargers,
          ]),
          encode: { x: [0, 1, 2], y: 3 },
          renderItem: (_params, api) => {
            const cat = api.value(3) as number
            const mid = api.coord([api.value(2) as number, cat])
            const y = mid[1] ?? 0
            const xLo = api.coord([api.value(0) as number, cat])[0] ?? 0
            const xHi = api.coord([api.value(1) as number, cat])[0] ?? 0
            const xMid = mid[0] ?? 0
            const solid = api.value(4) === 1
            // 점 크기 = √충전기수. 충전기 1기짜리가 1위로 보이는 오독을 막는다
            const r = markSize(api.value(5) as number, 3.5, 11, 0.06)
            const cap = Math.max(3, r - 1)
            const whisker = { stroke: ink3, lineWidth: 1.5 }
            return {
              type: 'group',
              children: [
                { type: 'line', shape: { x1: xLo, y1: y, x2: xHi, y2: y }, style: whisker },
                {
                  type: 'line',
                  shape: { x1: xLo, y1: y - cap, x2: xLo, y2: y + cap },
                  style: whisker,
                },
                {
                  type: 'line',
                  shape: { x1: xHi, y1: y - cap, x2: xHi, y2: y + cap },
                  style: whisker,
                },
                {
                  type: 'circle',
                  shape: { cx: xMid, cy: y, r },
                  // 겹치는 마크에는 2px 표면 링 — 구간선 위에서도 점이 읽힌다
                  style: solid
                    ? { fill: accent, stroke: canvas, lineWidth: 2 }
                    : { fill: canvas, stroke: accent, lineWidth: 2 },
                },
              ],
            }
          },
          markLine:
            overall != null
              ? {
                  silent: true,
                  symbol: 'none',
                  lineStyle: { color: ink3, type: 'solid', width: 1 },
                  label: {
                    formatter: `전체 ${pct(overall)}`,
                    fontSize: 10,
                    color: ink3,
                    position: 'end',
                    rotate: 0,
                    distance: 4,
                  },
                  data: [{ xAxis: overall }],
                }
              : undefined,
        },
        {
          // 축이 이상치 하나에 눌리면 상위권이 한 점으로 뭉친다. 축은 모양을
          // 맡고, 정확한 값은 각 행 오른쪽에 직접 적는다 (툴팁 없이도 읽힌다)
          type: 'scatter',
          symbolSize: 0,
          data: rows.map((_r, i) => [band.max, i]),
          label: {
            show: true,
            position: 'right',
            distance: 8,
            fontSize: 11,
            color: cssVar('--c-ink-2', '#4a4a4f'),
            formatter: (p: { dataIndex: number }) => pct(rows[p.dataIndex]?.availability, 2),
          },
          silent: true,
        },
      ],
    }
  }, [shown, overall, entity, labelOf, bandShown])

  const table = (
    <>
      <div className="ctable">
        <table className="data-table">
          <caption className="sr-only">순위 비교 — 값과 95% 신뢰구간</caption>
          <thead>
            <tr>
              <th scope="col" title={`정렬: ${sortMeta.label}`}>
                #
              </th>
              <th scope="col">대상</th>
              <th scope="col" className="ta-r">
                충전기
              </th>
              <th scope="col" className="ta-r">
                가동률
              </th>
              <th scope="col" className="ta-r">
                95% 구간
              </th>
              <th scope="col" className="ta-r">
                커버리지
              </th>
              <th scope="col" className="ta-r">
                관측량
              </th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((r, i) => (
              <tr key={r.entity_id}>
                <td className="num">{i + 1}</td>
                <td>{labelOf(entity, r.entity_id)}</td>
                <td className="num ta-r">{int(r.n_chargers)}</td>
                <td className="num ta-r">{pct(r.availability)}</td>
                <td className="num ta-r">
                  {pct(r.availability_ci[0])} ~ {pct(r.availability_ci[1])}
                </td>
                <td className="num ta-r">{pct(r.coverage, 1)}</td>
                <td className="num ta-r">{minutes(r.t_obs_min)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {excluded.length > 0 && (
        <details className="excl">
          <summary>
            순위에서 분리된 대상 <span className="num">{excluded.length}</span>건 — 숨기지 않고 사유와
            함께 남깁니다
          </summary>
          <div className="ctable">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">대상</th>
                  <th scope="col" className="ta-r">
                    충전기
                  </th>
                  <th scope="col" className="ta-r">
                    커버리지
                  </th>
                  <th scope="col">판정</th>
                  <th scope="col">사유</th>
                </tr>
              </thead>
              <tbody>
                {excluded.map((r) => (
                  <tr key={r.entity_id}>
                    <td>{labelOf(entity, r.entity_id)}</td>
                    <td className="num ta-r">{int(r.n_chargers)}</td>
                    <td className="num ta-r">{pct(r.coverage, 1)}</td>
                    <td>{r.verdict ? VERDICT_LABEL[r.verdict] : '커버리지 미달'}</td>
                    <td className="excl__why">{r.quality_reasons ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </>
  )

  return (
    <ChartFrame
      title="가동률 순위와 95% 신뢰구간"
      question="가동률과 95% 신뢰구간 — 구간이 겹치면 순위를 주장할 수 없다"
      meta={env?.meta}
      headline={
        ranked.length > 0 ? (
          <Headline
            value={`${nDistinct}/${ranked.length}`}
            unit="전체 가동률과 구분되는 대상"
          />
        ) : undefined
      }
      reading={`${axisLabel}별 가동률을 점으로, 그 값이 얼마나 흔들릴 수 있는지를 좌우 수염으로 그렸습니다. 점이 오른쪽일수록 가동률이 높습니다. 수염이 서로 겹치는 두 대상은 순서를 말할 수 없습니다.`}
      encoding={[
        {
          role: '가로축',
          what: '가동률 (%)',
          detail:
            '관측된 시간 중 UP 이었던 비율입니다. 관측되지 않은 시간은 분모에 넣지 않습니다. 0% 부터 그리지 않고 값이 있는 구간만 확대했는데, 점은 면적이 없어 축을 잘라도 길이가 거짓말하지 않기 때문입니다 — 같은 이유로 막대를 쓰지 않았습니다.',
        },
        {
          role: '세로축',
          what: `${axisLabel} · ${sortMeta.axis}`,
          detail:
            sort === 'lower'
              ? '관측량을 감안한 하한으로 세웠습니다. 관측이 얇은 대상은 하한이 낮아 자연히 내려갑니다 — 충전기 1기짜리가 최상단을 차지하지 않습니다.'
              : '측정된 값 그대로 세웠습니다. 관측이 얇은 대상이 위에 올 수 있으므로 점 크기와 수염을 함께 보세요.',
        },
        {
          role: '점 크기',
          what: '충전기 수 (지름 ∝ √n)',
          detail:
            '작은 점이 위에 있으면 "몇 대 안 되는데 운이 좋았다"를 의심해야 합니다. 충전기 1기짜리도 가동률 100%가 될 수 있습니다.',
        },
        {
          role: '수염',
          what: '95% 신뢰구간',
          detail:
            'Wilson 구간이며 관측 시간(분)을 시행 횟수로 씁니다. 관측량이 많으면 구간이 점보다 작아져 보이지 않습니다 — 정확한 폭은 표 보기에 있습니다.',
        },
        {
          role: '세로선',
          what: '전체 가동률',
          detail: '백엔드가 계산한 전체 값입니다. 프론트에서 평균을 다시 내지 않습니다.',
        },
        {
          role: '점 채움',
          what: '전체와 구분 가능한가',
          detail:
            '채운 점은 구간이 세로선을 넘지 않는다는 뜻으로, "전체와 다르다"고 말할 수 있습니다. 빈 점은 그 차이가 관측량으로 설명될 수 있어 말할 수 없습니다.',
        },
        {
          role: '오른쪽 숫자',
          what: '가동률 값 (소수 둘째 자리)',
          detail:
            '축이 이상치 하나에 눌리면 상위권이 한 점으로 뭉치므로, 정확한 값을 행마다 직접 적었습니다.',
        },
      ]}
      legend={[
        {
          label: '전체와 구분됨 (구간이 기준선을 넘지 않음)',
          color: cssVar('--c-accent', '#0066cc'),
          pattern: 'dot',
        },
        {
          label: '구분 못 함 (구간이 기준선을 가로지름)',
          color: cssVar('--c-accent', '#0066cc'),
          pattern: 'ring',
        },
      ]}
      controls={
      <div className="rank__sort" role="group" aria-label="정렬 기준">
          <span className="rank__sortlabel">정렬</span>
          {SORTS.map((x) => (
            <button
              key={x.key}
              type="button"
              className={`rank__sortbtn${sort === x.key ? ' is-on' : ''}`}
              aria-pressed={sort === x.key}
              onClick={() => setSort(x.key)}
            >
              {x.label}
            </button>
          ))}
          <span className="rank__sorthint">
            {sort === 'lower'
              ? '관측이 얇은 대상은 하한이 낮아 내려갑니다'
              : '충전기 몇 대뿐인 대상이 위에 올 수 있습니다'}
          </span>
        </div>
      }
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={ranked.length === 0 && excluded.length === 0}
      onRetry={() => void q.refetch()}
      table={table}
      notes={[
        {
          label: '순위는 점 하나가 아니라 구간이다.',
          text: '두 대상의 수염이 겹치면 "1위가 2위보다 낫다"고 말할 수 없습니다. 관측이 더 쌓이면 순서가 뒤집힐 수 있다는 뜻입니다.',
        },
        {
          label: '축을 확대했다.',
          text: `현재 가로축은 ${pct(bandShown.min, 0)}~${pct(bandShown.max, 0)} 구간만 보여줍니다. 전국 가동률이 좁은 범위에 몰려 있어 0%부터 그리면 모든 점이 한 자리에 겹칩니다.`,
        },
        {
          label: '정렬 기준을 바꿀 수 있다.',
          text: '"값 순"은 측정값 그대로라 관측이 얇은 대상이 위에 올 수 있습니다. "신뢰 하한 순"은 관측량을 감안해 "최소 이만큼은 된다"로 세우므로 몇 대뿐인 대상이 최상단을 차지하지 않습니다.',
        },
        {
          label: '커버리지 기준을 통과한 대상만 순위에 넣는다.',
          text: '통과하지 못한 대상은 버리지 않고 표 보기 아래에 사유와 함께 남깁니다.',
        },
        ranked.length > TOP_N
          ? {
              label: `상위 ${TOP_N}개만 그린다.`,
              text: `나머지 ${ranked.length - TOP_N}개는 표 보기에 있습니다.`,
            }
          : '',
      ]}
    >
      {ranked.length === 0 ? (
        <div className="verdict-note" role="status">
          <p>
            <strong>이 축에서는 순위를 낼 수 없습니다.</strong> {excluded.length}개 대상 전부가
            비교 조건을 통과하지 못했습니다.
          </p>
          <p className="verdict-note__hint">
            "값이 없다"가 아니라 <em>"아직 판정할 수 없다"</em>입니다. 사유는 표 보기에서 대상별로
            확인할 수 있습니다.
          </p>
        </div>
      ) : (
        <EChart
          option={option}
          height={Math.max(180, shown.length * 26 + 40)}
          ariaLabel={`가동률 순위 점 그래프. 상위 ${shown.length}개. 1위 ${
            shown[0] ? labelOf(entity, shown[0].entity_id) : ''
          } ${pct(shown[0]?.availability)}. 전체 기준 ${pct(overall)}. 정확한 값은 표 보기에 있습니다.`}
        />
      )}
    </ChartFrame>
  )
}
